<script lang="ts" module>
  // import { ApiError } from '@/teoCore/errors/ApiError';
  import { ApiError } from '@/teoCore/errors/ApiError';
  import { getPresetLoadingMessage } from '@/teoCore/text/AIHandler/aiSuggestionPresets';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import SmallGhostButton from '@/ui/elements/buttons/SmallGhostButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useAtomValue } from '@/utils/jotai-svelte';
  import { ActiveText$, onMountWithActiveText, teoAtom, useTeoCore } from '@/utils/teo-context';

  import AbTestComponent from './ABTestComponent.svelte';
  import AISuggestionTestComponent from './AISuggestionTestComponent.svelte';
  import SentimentTestComponent from './SentimentTestComponent.svelte';

  const AnalysisInstructions$ = teoAtom(
    (get) => get(ActiveText$).analysis?.getInstructions() ?? [],
  );

  AnalysisInstructions$.onMount = onMountWithActiveText(({ refresh }, activeText) => {
    return activeText.events.addEventListener('gotAnalysis', refresh);
  });

  const AISuggestion$ = teoAtom((get) => get(ActiveText$).ai.selection.lastSuggestionDebug);
  AISuggestion$.onMount = onMountWithActiveText(({ refresh }, activeText) => {
    return activeText.ai.selection.events.addEventListener('aiSuggestionReceived', refresh);
  });

  const QuickPrompts$ = teoAtom((get) => get(ActiveText$).ai.quickPrompts);
  QuickPrompts$.onMount = onMountWithActiveText(({ refresh }, activeText) => {
    return activeText.ai.events.addEventListener('quickPromptLogged', refresh);
  });
</script>

<script lang="ts">
  const core = useTeoCore();

  const instructions = useAtomValue(AnalysisInstructions$);
  // const aiSuggestion = useAtomValue(AISuggestion$);
  const quickPrompts = useAtomValue(QuickPrompts$);

  let evaluationProfiles: DropDownItem[] = $state([]);
  let selectedEvaluationProfileId: number | null = $state(
    core.dynamicConfig.options.evaluationProfileId,
  );

  function setExpParserOption(useExpParser: boolean | undefined) {
    core.dynamicConfig.setStateValue('useExpParser', useExpParser ?? false);
    core.activeText.touch();
  }

  function setTestFeaturesEnabled(enabled: boolean) {
    core.dynamicConfig.setStateValue('testFeaturesEnabled', enabled);
  }

  async function loadEvaluationProfiles() {
    evaluationProfiles = [];
    const result = await core.api.getEvaluationProfiles();
    if (!(result instanceof ApiError)) {
      evaluationProfiles = result.map((profile) => {
        return {
          label: profile.name,
          value: profile.id.toString(),
        };
      });
    }
  }

  function onEvaluationProfileSelect(item: string) {
    const profileId = parseInt(item);
    selectedEvaluationProfileId = profileId;
    core.dynamicConfig.setStateValue('evaluationProfileId', profileId);
    // core.activeText.touch();
  }

  function copyPrompt(event: MouseEvent, prompt: string): void {
    event.preventDefault();
    event.stopPropagation();
    void navigator.clipboard.writeText(prompt);
  }

  // function updateDiffFull() {
  //   core.activeText.diff.update(core.activeText.text, true);
  // }

  // function updateDiffMarkers() {
  //   core.activeText.diff.update();
  // }

  // function showDiff() {
  //   core.activeText.diff.show();
  // }
  // function hideDiff() {
  //   core.activeText.diff.hide();
  // }
</script>

<details>
  <summary>
    <strong>Test Features</strong>
  </summary>

  <div class="grid gap-2">
    <div class="flex flex-col gap-2">
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          checked={core.dynamicConfig.options.useExpParser}
          onchange={(e) => setExpParserOption((e.target as HTMLInputElement).checked)}
        />
        Use ExpParser
      </label>
      <label class="flex items-center gap-2">
        <input
          type="checkbox"
          checked={core.dynamicConfig.options.testFeaturesEnabled}
          onchange={(e) => setTestFeaturesEnabled((e.target as HTMLInputElement).checked)}
        />
        UI Tests
      </label>
      {#if __NET_RECORDER__}
        {#await import('@/devtools/netRecorder/NetRecorderSwitch.svelte') then { default: NetRecorderSwitch }}
          <NetRecorderSwitch />
        {/await}
      {/if}
    </div>
    <!-- <CtaButton variant="primary" onclick={updateDiffFull}>Set diff Text</CtaButton>
    <CtaButton variant="primary" onclick={updateDiffMarkers}>Update diff markers</CtaButton>
    <CtaButton variant="primary" onclick={showDiff}>Show diff</CtaButton>
    <CtaButton variant="primary" onclick={hideDiff}>Hide diff</CtaButton> -->
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Evaluation Profile</strong>
  </summary>
  <div class="mt-2 flex items-center gap-2">
    <DropDown
      class="w-full flex-1"
      items={evaluationProfiles ?? []}
      disabled={!evaluationProfiles || evaluationProfiles.length === 0}
      selected={selectedEvaluationProfileId ? selectedEvaluationProfileId.toString() : ''}
      onItemClick={onEvaluationProfileSelect}
    ></DropDown>
    <CtaButton
      variant="cta"
      icon="Sync"
      tooltip="Profile laden"
      class="ml-2"
      onclick={() => loadEvaluationProfiles()}
    ></CtaButton>
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Handlunsanweisungen</strong>
  </summary>

  <div class="mt-2 flex h-full flex-col gap-2 overflow-auto">
    {#if instructions.current.length === 0}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">No instructions available</div>
    {/if}
    {#each instructions.current as instruction (instruction)}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <div>
          {instruction.title}
        </div>
        <div>
          {instruction.priority_score.toFixed(2)}
          ({instruction.expected_tpi_change.toFixed(2)})
        </div>
      </div>
    {/each}
  </div>
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Factor168</strong>
  </summary>
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    <SentimentTestComponent />
  </div>
  <hr class="mb-2 mt-2" />
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    <AbTestComponent />
  </div>
</details>
<!-- <hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Letzte KI Antwort</strong>
  </summary>
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    {#if aiSuggestion.current === null}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">No AI suggestions available</div>
    {:else if aiSuggestion.current.suggestions.length}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <div>
          {aiSuggestion.current.suggestions[0]?.text}
        </div>
      </div>
    {/if}
  </div>
</details> -->
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>KI-Anweisung testen</strong>
  </summary>
  <AISuggestionTestComponent />
</details>
<hr class="mb-2 mt-2" />
<details>
  <summary>
    <strong>Auto-Modus-Prompts</strong>
  </summary>
  <div class="mt-2 flex h-full w-full flex-col gap-2 overflow-auto">
    {#if quickPrompts.current.length === 0}
      <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">Keine Prompts verfügbar</div>
    {/if}
    {#each quickPrompts.current as entry (entry)}
      <details class="bg-c-bg text-c-text rounded-md p-2 text-sm">
        <summary class="flex items-center gap-1">
          <span class="min-w-0 flex-1">
            {entry.time.toLocaleTimeString()} · {entry.preset ?
              getPresetLoadingMessage(entry.preset)
            : 'Eigener User Prompt'}
          </span>
          <SmallGhostButton
            icon="ContentCopy"
            label="Prompt kopieren"
            onclick={(event) => copyPrompt(event, entry.prompt)}
          />
        </summary>
        <pre class="mt-2 whitespace-pre-wrap text-xs">{entry.prompt}</pre>
      </details>
    {/each}
  </div>
</details>

<style>
  summary {
    cursor: pointer;
  }
</style>
